<script setup>
import { ref, onMounted } from 'vue'
import { useConversationsStore } from '@/stores/conversations'
import { useMessagesStore } from '@/stores/messages'
import { useExplanationStore } from '@/stores/explanations'
import ConversationElement from '@/components/conversations/ConversationElement.vue'
import NewScenarioModal from '@/components/conversations/NewScenarioModal.vue'
import DeleteConfirmationModal from '@/components/conversations/DeleteConfirmationModal.vue'

const conversationsStore = useConversationsStore()
const messagesStore = useMessagesStore()
const explanationStore = useExplanationStore()
const conversationToDeleteId = ref(null)
const isNewScenarioModalOpen = ref(false)
const isDeleteModalOpen = ref(false)
const isCreatingConversation = ref(false)
const errorCreatingConversation = ref('')

// Fetch conversations when the component loads
onMounted(() => {
  conversationsStore.fetchConversations()
})

// Handle conversation selection and fetch messages
async function selectConversation (conversationId) {
  explanationStore.clearSelectedMessage()
  messagesStore.fetchMessages(conversationId)
}

function openNewScenarioModal () {
  isNewScenarioModalOpen.value = true
}

function closeNewScenarioModal () {
  isNewScenarioModalOpen.value = false
}

function openDeleteModal (conversationId) {
  console.log('openDeleteModal', conversationId)
  conversationToDeleteId.value = conversationId
  isDeleteModalOpen.value = true
}

function closeDeleteModal () {
  isDeleteModalOpen.value = false
  conversationToDeleteId.value = null
}

// Handle new conversation creation
async function createConversation ({ language, scenario, difficulty }) {
  isCreatingConversation.value = true
  try {
    const newConversation = await conversationsStore.postConversation(language, scenario, difficulty)
    closeNewScenarioModal()
    // document.querySelector('body > div[modal-backdrop]')?.remove()
    // Open the newly created conversation
    messagesStore.setSelectedConversation(newConversation)
  } catch (error) {
    console.error('Error creating conversation:', error)
    errorCreatingConversation.value = error.message || 'Failed to create conversation'
  } finally {
    console.log('finally setting isCreatingConversation to false')
    isCreatingConversation.value = false
  }
}

// Handle conversation deletion
async function deleteConversation () {
  console.log('deleteConversation', conversationToDeleteId.value)
  if (!conversationToDeleteId.value) return

  try {
    // Clear selection if deleted conversation was active
    if (messagesStore.currentConversation?.conversation_id === conversationToDeleteId.value) {
      explanationStore.clearSelectedMessage()
      messagesStore.resetState()
    }

    await conversationsStore.deleteConversation(conversationToDeleteId.value)
  } catch (error) {
    console.error('Error deleting conversation:', error)
  } finally {
    closeDeleteModal()
  }
}
</script>

<template>
  <!-- Sidebar -->
  <aside class="relative bg-gray-50 p-2 pb-0 border-r h-[calc(100vh-4rem)] overflow-y-auto">
    <h2 class="text-xl font-bold mb-4">{{ $t('conversationsSidebar.title') }}</h2>
    <div class="mt-4">
      <!-- Conversation list -->
      <ConversationElement
        v-for="conversation in conversationsStore.conversations"
        :key="conversation.conversation_id"
        :conversation="conversation"
        :is-selected="messagesStore.currentConversation != null && messagesStore.currentConversation.conversation_id === conversation.conversation_id"
        @select="selectConversation"
        @delete="openDeleteModal"
      />
    </div>
    <!-- New Scenario button-->
    <div class="sticky bottom-0 w-full mt-4 p-2 bg-white pt-4 pb-4">
      <button class="w-full mt-1 p-2 bg-paletteDarkGreen text-white rounded-md" type="button"
        @click="openNewScenarioModal">
        {{ $t('conversationsSidebar.newScenarioButton') }}
      </button>
    </div>

    <!-- New Scenario Modal -->
    <NewScenarioModal
      :supported-languages="conversationsStore.supportedLanguages"
      :supported-scenarios="conversationsStore.supportedScenarios"
      :supported-difficulties="conversationsStore.supportedDifficulties"
      :is-open="isNewScenarioModalOpen"
      :is-creating="isCreatingConversation"
      :error-creating="errorCreatingConversation"
      @create="createConversation"
      @close="closeNewScenarioModal"
    />

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :is-open="isDeleteModalOpen"
      @confirm="deleteConversation"
      @cancel="closeDeleteModal"
    />
  </aside>
</template>

<style scoped>
.flag-icon {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}
</style>
